import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  ChevronLeft, ChevronRight, Play, Pause, RotateCcw, X,
  MessageSquare, Users, DollarSign, Sparkles, CheckCircle2,
  Scissors, Building2, Crown, Star, Heart, Zap, Shield,
  Gift, Target, Award, Smartphone, Calendar, CreditCard,
  ThumbsUp, AlertCircle, Copy, ExternalLink, BookOpen,
  Mic, UserPlus, TrendingUp, Check, Banknote, Globe, QrCode
} from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';

// Competitor comparison data - ACCURATE as of 2025
const competitorComparison = [
  { 
    feature: "Transaction Fees", 
    myHairChair: "0% (CashApp/Zelle/Cash)", 
    square: "2.6% + 10¢", 
    vagaro: "2.75%", 
    boulevard: "2.49% + 15¢",
    highlight: true
  },
  { 
    feature: "Monthly Subscription", 
    myHairChair: "$29.99/mo (Pro)", 
    square: "$29+/mo", 
    vagaro: "$25+/mo", 
    boulevard: "$175+/mo",
    highlight: true
  },
  { 
    feature: "Online Booking", 
    myHairChair: "✓", 
    square: "✓", 
    vagaro: "✓", 
    boulevard: "✓" 
  },
  { 
    feature: "Client Management", 
    myHairChair: "✓", 
    square: "✓", 
    vagaro: "✓", 
    boulevard: "✓" 
  },
  { 
    feature: "Text Reminders", 
    myHairChair: "✓", 
    square: "✓ (extra)", 
    vagaro: "✓", 
    boulevard: "✓" 
  },
  { 
    feature: "Payment Flexibility", 
    myHairChair: "CashApp/Zelle/Cash/Card", 
    square: "Card only", 
    vagaro: "Card only", 
    boulevard: "Card only",
    highlight: true
  },
  { 
    feature: "Built-in Booth Rent", 
    myHairChair: "✓", 
    square: "✗", 
    vagaro: "Limited", 
    boulevard: "✓" 
  },
  { 
    feature: "Digital Marketing Tools", 
    myHairChair: "✓ (Free)", 
    square: "✗", 
    vagaro: "Extra $", 
    boulevard: "Extra $" 
  },
  { 
    feature: "Look Contests", 
    myHairChair: "✓ (Win $$$)", 
    square: "✗", 
    vagaro: "✗", 
    boulevard: "✗",
    highlight: true
  },
  { 
    feature: "Free Trial", 
    myHairChair: "7 Days + Soldier Bonus", 
    square: "30 days", 
    vagaro: "30 days", 
    boulevard: "Demo only" 
  },
];

// Playbook slides
const playbookSlides = [
  {
    id: 'welcome',
    title: "Welcome to the Movement! 💪",
    subtitle: "You're not just a salesperson - you're a liberator",
    type: 'intro',
    content: {
      mainMessage: "Your mission: Help beauty professionals KEEP 100% of their hard-earned money",
      points: [
        "You're helping people escape 2-3% fees on EVERY transaction",
        "A stylist making $5,000/month loses $100-150/month to fees alone",
        "That's $1,200-1,800/year they could be keeping!",
        "You're not selling - you're solving a real problem"
      ],
      vibe: "Keep it real, keep it fun. We're in the beauty industry - it's creative, it's social, it's vibrant!"
    }
  },
  {
    id: 'opener',
    title: "The Opening Conversation",
    subtitle: "Start natural, stay curious",
    type: 'script',
    content: {
      scenario: "You see a stylist/barber working or you're chatting with one",
      scripts: [
        {
          label: "Casual Opener",
          text: "Hey! Love the vibe in here. Quick question - do you use any kind of booking app for your clients?",
          note: "Just be genuinely curious, not salesy"
        },
        {
          label: "If They Say YES",
          text: "Oh nice! Which one? [They answer] Cool, that's a solid app! How's it been treating you?",
          note: "NEVER bash their current app - let them tell you the pain points"
        },
        {
          label: "If They Say NO",
          text: "Gotcha! So you're doing it all old school - texting, DMs, calls? That's dedication! Ever thought about a system that could handle all that for you?",
          note: "Acknowledge their hustle first"
        }
      ]
    }
  },
  {
    id: 'discovery',
    title: "Discovery Questions",
    subtitle: "Listen more than you talk",
    type: 'questions',
    content: {
      intro: "Ask these to understand their situation:",
      questions: [
        {
          question: "How do most of your clients pay you?",
          why: "If they say CashApp/Zelle/Cash, they'll LOVE our zero fees",
          followUp: "Perfect! Do you know how much those card fees add up?"
        },
        {
          question: "What's the most annoying part about managing bookings?",
          why: "Find their pain point - we probably solve it",
          followUp: "Ugh, that sounds frustrating. What if there was a way to fix that?"
        },
        {
          question: "Do you rent a booth or own your own place?",
          why: "Owners and renters have different needs",
          followUp: "Cool! [If owner] We have some special features for shop owners too."
        },
        {
          question: "How do you remind clients about their appointments?",
          why: "No-shows cost money - we have automated reminders",
          followUp: "Yeah, no-shows are the worst! What if your app sent reminders automatically?"
        }
      ]
    }
  },
  {
    id: 'problem',
    title: "The Problem We Solve",
    subtitle: "Paint the picture",
    type: 'problem',
    content: {
      headline: "Most booking apps take a cut of EVERY transaction",
      stats: [
        { label: "Square", fee: "2.6% + 10¢", monthly: "$130+ lost", yearly: "$1,560+ lost" },
        { label: "Vagaro", fee: "2.75%", monthly: "$137+ lost", yearly: "$1,650+ lost" },
        { label: "Boulevard", fee: "2.49% + 15¢", monthly: "$125+ lost", yearly: "$1,500+ lost" }
      ],
      note: "Based on $5,000/month in card transactions",
      punchline: "That's a vacation, new equipment, or just money in YOUR pocket instead of theirs!"
    }
  },
  {
    id: 'solution',
    title: "Our Solution: Keep the 100 💯",
    subtitle: "Zero fees on the payment methods you already use",
    type: 'solution',
    content: {
      mainFeature: {
        title: "0% Transaction Fees",
        description: "Accept CashApp, Zelle, or Cash with ZERO fees taken by us",
        icon: "Banknote"
      },
      otherFeatures: [
        { icon: "Calendar", title: "Easy Online Booking", desc: "Clients book 24/7 from their phone" },
        { icon: "Smartphone", title: "Auto Text Reminders", desc: "Reduce no-shows automatically" },
        { icon: "Users", title: "Client Management", desc: "Track visits, notes, preferences" },
        { icon: "Gift", title: "Loyalty Points", desc: "Reward your regulars automatically" },
        { icon: "TrendingUp", title: "Look Fest Contests", desc: "Win prizes for your best work" }
      ],
      cardOption: "Want card payments? We integrate with Stripe - standard rates apply, but YOU keep the rest"
    }
  },
  {
    id: 'demo-walkthrough',
    title: "Show & Tell Time! 📱",
    subtitle: "Quick demo walkthrough",
    type: 'demo',
    content: {
      intro: "Open the demo on your phone and walk them through:",
      steps: [
        {
          step: 1,
          title: "The Booking Page",
          show: "Show how clean and easy it is to book",
          say: "This is what your clients see - super simple, right? They pick a service, pick a time, done."
        },
        {
          step: 2,
          title: "Payment Options",
          show: "Point out CashApp, Zelle, Cash options",
          say: "See this? CashApp, Zelle, Cash - zero fees on all of these. You keep everything."
        },
        {
          step: 3,
          title: "Automatic Reminders",
          show: "Explain the reminder system",
          say: "Your clients get automatic text reminders - less no-shows without you lifting a finger."
        },
        {
          step: 4,
          title: "Your Dashboard",
          show: "Give a peek at the professional dashboard",
          say: "This is your command center - see all your bookings, earnings, client history, everything in one place."
        }
      ],
      demoLink: "/demo?package=pro"
    }
  },
  {
    id: 'comparison',
    title: "The Comparison",
    subtitle: "Only show this if they ask or seem interested",
    type: 'comparison',
    content: {
      tip: "Don't bash competitors - just show the facts and let them decide",
      note: "If they love their current app, that's fine! Plant the seed and move on."
    }
  },
  {
    id: 'objections',
    title: "Handling Pushback",
    subtitle: "Stay cool, stay helpful",
    type: 'objections',
    content: {
      objections: [
        {
          objection: "I'm happy with what I have",
          response: "That's awesome! Sounds like you've got a good system. Just curious - do you know how much you're paying in fees each month? [If they don't know] Might be worth checking! Either way, here's my code if you ever want to try us out.",
          vibe: "Respect their choice, plant the seed"
        },
        {
          objection: "I don't want to switch everything",
          response: "Totally get it - switching is a pain. Good news is you can try us alongside what you're using now. No commitment. See which one works better for you.",
          vibe: "Remove the pressure"
        },
        {
          objection: "My clients are used to paying by card",
          response: "For sure! We work with Stripe for card payments too. But think about it - how many clients already pay you through CashApp or Zelle? Those transactions would be completely fee-free with us.",
          vibe: "Show flexibility"
        },
        {
          objection: "Is this legit?",
          response: "100%! We're a growing platform built BY people in the industry. Check out our Look Fest contest winners - real stylists winning real money. Here, let me show you...",
          vibe: "Social proof is powerful"
        },
        {
          objection: "I'll think about it",
          response: "Of course! No pressure at all. Here's my code - [YOUR CODE]. You get $10/mo off for your first 3 months. Take your time, I'll be around if you have questions!",
          vibe: "Leave them with value"
        }
      ]
    }
  },
  {
    id: 'close',
    title: "The Close",
    subtitle: "Make it easy for them",
    type: 'close',
    content: {
      setup: "If they're interested, here's your power move:",
      script: {
        intro: "Alright, so here's the deal...",
        offer: "If you sign up right now using my code, you get:",
        benefits: [
          "30 DAYS completely FREE to try everything out",
          "Then $10 OFF per month for your first 3 months after the trial",
          "Pro at $19.99/mo (vs $29.99) or Elite at $39.99/mo (vs $49.99)",
          "That's 30 days free PLUS $30+ in savings!"
        ],
        urgency: "My code is [YOUR REFERRAL CODE] - it's only valid through me as a Soldier, so this deal isn't something they can get from the website.",
        close: "Want me to help you set it up real quick? Takes like 2 minutes."
      },
      alternatives: [
        {
          scenario: "If they need more time",
          action: "No worries! Take my code down - [YOUR CODE]. When you're ready, just use it at signup. The 30 days free + intro pricing is always there for you."
        },
        {
          scenario: "If they want to talk to their partner/boss first",
          action: "Smart! Here, let me send you a text with my code and a link to check us out. What's your number?"
        }
      ]
    }
  },
  {
    id: 'followup',
    title: "The Follow-Up Game",
    subtitle: "Fortune is in the follow-up",
    type: 'followup',
    content: {
      rule: "If they didn't sign up, don't be pushy but DO follow up!",
      timeline: [
        {
          when: "Same day",
          action: "Send a friendly text: 'Hey [Name]! Great meeting you. Here's that code we talked about - [CODE]. Lmk if you have any questions! 💈'"
        },
        {
          when: "3-4 days later",
          action: "Quick check-in: 'Hey! Did you get a chance to check out MyHairChair? Happy to hop on a call if you want a walkthrough!'"
        },
        {
          when: "1 week later",
          action: "Value add: 'Yo! We just launched a new feature [mention something cool]. Thought of you - still got that code saved for you 😎'"
        }
      ],
      dont: [
        "Don't spam them",
        "Don't be negative if they went with someone else",
        "Don't give up after one 'no' - timing matters!"
      ]
    }
  },
  {
    id: 'practice',
    title: "Practice Makes Perfect",
    subtitle: "Role-play scenarios",
    type: 'practice',
    content: {
      intro: "Practice these scenarios alone or with a friend:",
      scenarios: [
        {
          title: "The Busy Barber",
          setup: "They're cutting hair, seems slammed",
          approach: "Be quick, be respectful of their time",
          goal: "Get their contact, follow up later"
        },
        {
          title: "The Tech-Hesitant Stylist",
          setup: "They've never used a booking app",
          approach: "Focus on simplicity, offer to help them set up",
          goal: "Make technology feel accessible"
        },
        {
          title: "The Loyal Square User",
          setup: "They've used Square for years",
          approach: "Acknowledge what Square does well, focus on the fee savings",
          goal: "Plant the seed, don't force the switch"
        },
        {
          title: "The Salon Owner",
          setup: "They have multiple stylists",
          approach: "Talk about team management, booth rent features",
          goal: "Position as a business solution, not just booking"
        }
      ],
      tips: [
        "Record yourself and listen back",
        "Practice in front of a mirror",
        "Time yourself - keep the pitch under 2 minutes",
        "Have a friend play the skeptic"
      ]
    }
  },
  {
    id: 'success',
    title: "You Got This! 🎯",
    subtitle: "Final reminders",
    type: 'success',
    content: {
      mindset: [
        "You're helping people keep more of their money",
        "Every conversation is practice - even the 'no's",
        "Building relationships > making quick sales",
        "Your authenticity is your superpower"
      ],
      yourOffer: {
        code: "[YOUR REFERRAL CODE]",
        value: "14 Days FREE + $10 off for 3 months",
        commission: "You earn commission on every signup!"
      },
      resources: [
        "Demo packages in your Soldier Dashboard",
        "Social media cards ready to share",
        "QR codes for quick signups",
        "This playbook - anytime you need it"
      ]
    }
  }
];

export default function SoldierRecruitmentPlaybook({ 
  isOpen, 
  onClose, 
  referralCode = "YOUR-CODE",
  discountValue = "$10/mo off for 3 months" 
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [showComparison, setShowComparison] = useState(false);

  if (!isOpen) return null;

  const slide = playbookSlides[currentSlide];
  const progress = ((currentSlide + 1) / playbookSlides.length) * 100;

  const nextSlide = () => {
    if (currentSlide < playbookSlides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success("Referral code copied!");
  };

  // Replace placeholder code in scripts
  const replaceCode = (text) => {
    return text
      .replace(/\[YOUR CODE\]/g, referralCode)
      .replace(/\[YOUR REFERRAL CODE\]/g, referralCode)
      .replace(/\[CODE\]/g, referralCode);
  };

  const renderSlideContent = () => {
    switch (slide.type) {
      case 'intro':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-400 mb-4">{slide.content.mainMessage}</p>
            </div>
            <div className="grid gap-3">
              {slide.content.points.map((point, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-zinc-800/50 p-4 rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <p className="text-zinc-200">{point}</p>
                </div>
              ))}
            </div>
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 p-4 rounded-xl">
              <p className="text-purple-300 text-sm italic flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                {slide.content.vibe}
              </p>
            </div>
          </div>
        );

      case 'script':
        return (
          <div className="space-y-6">
            <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-xl">
              <p className="text-amber-300 text-sm flex items-center gap-2">
                <Target className="w-4 h-4" />
                <span className="font-medium">Scenario:</span> {slide.content.scenario}
              </p>
            </div>
            <div className="space-y-4">
              {slide.content.scripts.map((script, idx) => (
                <Card key={idx} className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-3">
                      {script.label}
                    </Badge>
                    <p className="text-white text-lg mb-3 font-medium leading-relaxed">
                      {`"${replaceCode(script.text)}"`}
                    </p>
                    <p className="text-zinc-400 text-sm flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      {script.note}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case 'questions':
        return (
          <div className="space-y-6">
            <p className="text-zinc-300 text-center">{slide.content.intro}</p>
            <div className="space-y-4">
              {slide.content.questions.map((q, idx) => (
                <Card key={idx} className="bg-zinc-800/50 border-zinc-700 overflow-hidden">
                  <CardContent className="p-0">
                    <div className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 p-4 border-b border-zinc-700">
                      <p className="text-white font-semibold flex items-center gap-2">
                        <Mic className="w-4 h-4 text-amber-400" />
                        {`"${q.question}"`}
                      </p>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="flex items-start gap-2">
                        <Badge variant="outline" className="text-green-400 border-green-500/30 text-xs">WHY</Badge>
                        <p className="text-zinc-300 text-sm">{q.why}</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <Badge variant="outline" className="text-blue-400 border-blue-500/30 text-xs">THEN</Badge>
                        <p className="text-zinc-300 text-sm">{`"${q.followUp}"`}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case 'problem':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <p className="text-xl font-bold text-red-400 mb-2">{slide.content.headline}</p>
              <p className="text-zinc-500 text-sm">{slide.content.note}</p>
            </div>
            <div className="grid gap-3">
              {slide.content.stats.map((stat, idx) => (
                <div key={idx} className="bg-red-500/10 border border-red-500/30 p-4 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-bold">{stat.label}</p>
                      <p className="text-red-400 text-sm">Fee: {stat.fee}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-red-400 font-bold">{stat.monthly}</p>
                      <p className="text-red-300 text-sm">{stat.yearly}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 p-4 rounded-xl text-center">
              <p className="text-green-400 font-bold text-lg">{slide.content.punchline}</p>
            </div>
          </div>
        );

      case 'solution':
        return (
          <div className="space-y-6">
            {/* Main Feature */}
            <div className="bg-gradient-to-r from-amber-500/30 to-yellow-500/30 border border-amber-500/50 p-6 rounded-2xl text-center">
              <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Banknote className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">{slide.content.mainFeature.title}</h3>
              <p className="text-amber-200">{slide.content.mainFeature.description}</p>
            </div>

            {/* Other Features Grid */}
            <div className="grid grid-cols-2 gap-3">
              {slide.content.otherFeatures.map((feature, idx) => {
                const icons = { Calendar, Smartphone, Users, Gift, TrendingUp };
                const Icon = icons[feature.icon] || Star;
                return (
                  <div key={idx} className="bg-zinc-800/50 border border-zinc-700 p-3 rounded-xl">
                    <Icon className="w-5 h-5 text-amber-400 mb-2" />
                    <p className="text-white font-medium text-sm">{feature.title}</p>
                    <p className="text-zinc-400 text-xs">{feature.desc}</p>
                  </div>
                );
              })}
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-xl">
              <p className="text-blue-300 text-sm flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                {slide.content.cardOption}
              </p>
            </div>
          </div>
        );

      case 'demo':
        return (
          <div className="space-y-6">
            <p className="text-zinc-300 text-center">{slide.content.intro}</p>
            <div className="space-y-4">
              {slide.content.steps.map((step, idx) => (
                <Card key={idx} className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-black font-bold">
                        {step.step}
                      </div>
                      <h4 className="text-white font-bold">{step.title}</h4>
                    </div>
                    <div className="space-y-2 pl-11">
                      <div className="flex items-start gap-2">
                        <Badge variant="outline" className="text-purple-400 border-purple-500/30 text-xs">SHOW</Badge>
                        <p className="text-zinc-300 text-sm">{step.show}</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <Badge variant="outline" className="text-green-400 border-green-500/30 text-xs">SAY</Badge>
                        <p className="text-zinc-300 text-sm">{`"${step.say}"`}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Button 
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white"
              onClick={() => window.open(slide.content.demoLink, '_blank')}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Demo to Show
            </Button>
          </div>
        );

      case 'comparison':
        return (
          <div className="space-y-6">
            <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-xl">
              <p className="text-amber-300 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {slide.content.tip}
              </p>
            </div>
            
            <Button 
              onClick={() => setShowComparison(!showComparison)}
              className="w-full bg-zinc-800 hover:bg-zinc-700 text-white"
            >
              {showComparison ? 'Hide' : 'Show'} Comparison Chart
            </Button>

            {showComparison && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-700">
                      <th className="text-left p-2 text-zinc-400">Feature</th>
                      <th className="text-center p-2 text-amber-400 bg-amber-500/10">MyHairChair</th>
                      <th className="text-center p-2 text-zinc-400">Square</th>
                      <th className="text-center p-2 text-zinc-400">Vagaro</th>
                      <th className="text-center p-2 text-zinc-400">Boulevard</th>
                    </tr>
                  </thead>
                  <tbody>
                    {competitorComparison.map((row, idx) => (
                      <tr key={idx} className={`border-b border-zinc-800 ${row.highlight ? 'bg-green-500/5' : ''}`}>
                        <td className="p-2 text-zinc-300">{row.feature}</td>
                        <td className="p-2 text-center text-amber-400 bg-amber-500/10 font-medium">{row.myHairChair}</td>
                        <td className="p-2 text-center text-zinc-400">{row.square}</td>
                        <td className="p-2 text-center text-zinc-400">{row.vagaro}</td>
                        <td className="p-2 text-center text-zinc-400">{row.boulevard}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-zinc-500 text-xs text-center">{slide.content.note}</p>
          </div>
        );

      case 'objections':
        return (
          <div className="space-y-4">
            {slide.content.objections.map((obj, idx) => (
              <Card key={idx} className="bg-zinc-800/50 border-zinc-700">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-4 h-4 text-red-400" />
                    </div>
                    <p className="text-red-300 font-medium">{`"${obj.objection}"`}</p>
                  </div>
                  <div className="flex items-start gap-3 mb-3 pl-11">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <ThumbsUp className="w-3 h-3 text-green-400" />
                    </div>
                    <p className="text-green-300 text-sm">{`"${replaceCode(obj.response)}"`}</p>
                  </div>
                  <p className="text-zinc-500 text-xs pl-11 italic flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Vibe: {obj.vibe}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        );

      case 'close':
        const signupUrl = `${window.location.origin}/lookfest?soldier=${referralCode}`;
        return (
          <div className="space-y-6">
            <p className="text-zinc-300 text-center">{slide.content.setup}</p>
            
            <Card className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border-amber-500/50">
              <CardContent className="p-6">
                <p className="text-zinc-300 mb-2">{`"${slide.content.script.intro}"`}</p>
                <p className="text-white font-bold mb-4">{`"${slide.content.script.offer}"`}</p>
                <div className="space-y-2 mb-4">
                  {slide.content.script.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                      <p className="text-amber-200">{benefit}</p>
                    </div>
                  ))}
                </div>
                <p className="text-zinc-300 text-sm mb-4">{`"${replaceCode(slide.content.script.urgency)}"`}</p>
                <p className="text-white font-bold text-lg">{`"${slide.content.script.close}"`}</p>
              </CardContent>
            </Card>

            {/* QR Code + Code Display */}
            <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                {/* QR Code */}
                <div className="bg-white p-4 rounded-xl">
                  <QRCode value={signupUrl} size={150} />
                </div>
                
                {/* Code Info */}
                <div className="flex-1 text-center md:text-left">
                  <p className="text-zinc-400 text-sm mb-1">Your Soldier Code:</p>
                  <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
                    <code className="text-3xl font-mono font-bold text-amber-400">{referralCode}</code>
                    <Button size="sm" variant="ghost" onClick={copyCode} className="text-zinc-400">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3 mb-3">
                    <p className="text-green-400 font-bold flex items-center gap-2">
                      <Gift className="w-4 h-4" />
                      14-Day FREE Trial
                    </p>
                    <p className="text-green-300 text-sm">+ $10 off for first 3 months</p>
                  </div>
                  
                  <p className="text-zinc-500 text-xs flex items-center gap-1">
                    <QrCode className="w-3 h-3" />
                    Scan QR to sign up with your code automatically applied
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {slide.content.alternatives.map((alt, idx) => (
                <div key={idx} className="bg-zinc-800/50 border border-zinc-700 p-3 rounded-xl">
                  <p className="text-amber-400 text-sm font-medium mb-1">{alt.scenario}</p>
                  <p className="text-zinc-300 text-sm">{`"${replaceCode(alt.action)}"`}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'followup':
        return (
          <div className="space-y-6">
            <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-xl">
              <p className="text-amber-300 font-medium flex items-center gap-2">
                <Star className="w-4 h-4" />
                {slide.content.rule}
              </p>
            </div>

            <div className="space-y-3">
              {slide.content.timeline.map((item, idx) => (
                <Card key={idx} className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 mb-2">
                      {item.when}
                    </Badge>
                    <p className="text-zinc-300 text-sm">{replaceCode(item.action)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-xl">
              <p className="text-red-400 font-medium mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Don&#39;t:
              </p>
              <ul className="space-y-1">
                {slide.content.dont.map((item, idx) => (
                  <li key={idx} className="text-red-300 text-sm flex items-center gap-2">
                    <X className="w-3 h-3" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );

      case 'practice':
        return (
          <div className="space-y-6">
            <p className="text-zinc-300 text-center">{slide.content.intro}</p>
            
            <div className="grid gap-3">
              {slide.content.scenarios.map((scenario, idx) => (
                <Card key={idx} className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center">
                        <Users className="w-4 h-4 text-purple-400" />
                      </div>
                      <h4 className="text-white font-bold">{scenario.title}</h4>
                    </div>
                    <div className="space-y-1 pl-10 text-sm">
                      <p className="text-zinc-400"><span className="text-zinc-500">Setup:</span> {scenario.setup}</p>
                      <p className="text-zinc-400"><span className="text-zinc-500">Approach:</span> {scenario.approach}</p>
                      <p className="text-green-400"><span className="text-zinc-500">Goal:</span> {scenario.goal}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded-xl">
              <p className="text-blue-400 font-medium mb-2 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Practice Tips:
              </p>
              <ul className="space-y-1">
                {slide.content.tips.map((tip, idx) => (
                  <li key={idx} className="text-blue-300 text-sm flex items-center gap-2">
                    <Check className="w-3 h-3" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="space-y-6">
            {/* Mindset */}
            <div className="space-y-2">
              {slide.content.mindset.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 p-3 rounded-xl">
                  <Heart className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <p className="text-green-300">{item}</p>
                </div>
              ))}
            </div>

            {/* Your Offer Card with QR Code */}
            <Card className="bg-gradient-to-r from-amber-500/30 to-yellow-500/30 border-amber-500/50">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  {/* QR Code */}
                  <div className="bg-white p-4 rounded-xl shadow-lg">
                    <QRCode value={`${window.location.origin}/lookfest?soldier=${referralCode}`} size={140} />
                  </div>
                  
                  {/* Code Info */}
                  <div className="flex-1 text-center md:text-left">
                    <Crown className="w-10 h-10 text-amber-400 mx-auto md:mx-0 mb-2" />
                    <p className="text-zinc-400 text-sm mb-1">Your Exclusive Offer</p>
                    <code className="text-3xl font-mono font-bold text-amber-400 block mb-2">{referralCode}</code>
                    
                    <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3 mb-3 inline-block">
                      <p className="text-green-400 font-bold flex items-center gap-2 justify-center md:justify-start">
                        <Gift className="w-4 h-4" />
                        14-Day FREE Trial
                      </p>
                      <p className="text-green-300 text-sm">+ $10 off for 3 months</p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-2 justify-center md:justify-start">
                      <Button onClick={copyCode} className="bg-amber-500 hover:bg-amber-600 text-black">
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Code
                      </Button>
                      <Button 
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/lookfest?soldier=${referralCode}`);
                          toast.success("Signup link copied!");
                        }} 
                        variant="outline"
                        className="border-amber-500 text-amber-400 hover:bg-amber-500/10"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Copy Link
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Resources */}
            <div className="bg-zinc-800/50 border border-zinc-700 p-4 rounded-xl">
              <p className="text-white font-medium mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4 text-amber-400" />
                Your Resources:
              </p>
              <div className="grid grid-cols-2 gap-2">
                {slide.content.resources.map((resource, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-zinc-400 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    {resource}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return <p className="text-zinc-400">Content coming soon...</p>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 overflow-hidden">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-zinc-800 bg-zinc-950 p-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-xl flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-black" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">Soldier Recruitment Playbook</h1>
                <p className="text-zinc-500 text-xs">Your guide to building the movement</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={isPresentationMode ? "default" : "outline"}
                size="sm"
                onClick={() => setIsPresentationMode(!isPresentationMode)}
                className={isPresentationMode ? "bg-amber-500 text-black" : "border-zinc-700 text-zinc-400"}
              >
                {isPresentationMode ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                {isPresentationMode ? 'Exit' : 'Present'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setCurrentSlide(0); }}
                className="text-zinc-400"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-zinc-400"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="flex-shrink-0 bg-zinc-900 px-4 py-2">
          <div className="max-w-4xl mx-auto">
            <Progress value={progress} className="h-1" />
            <div className="flex items-center justify-between mt-2">
              <p className="text-zinc-500 text-xs">Step {currentSlide + 1} of {playbookSlides.length}</p>
              <p className="text-zinc-500 text-xs">{slide.title}</p>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-4 sm:p-6">
            {/* Slide Header */}
            <div className="text-center mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">{slide.title}</h2>
              <p className="text-zinc-400">{slide.subtitle}</p>
            </div>

            {/* Slide Content */}
            <div className="mb-8">
              {renderSlideContent()}
            </div>
          </div>
        </div>

        {/* Navigation Footer */}
        <div className="flex-shrink-0 border-t border-zinc-800 bg-zinc-950 p-4">
          <div className="max-w-4xl mx-auto">
            {/* Slide Dots */}
            <div className="flex justify-center gap-1 mb-4 overflow-x-auto pb-2">
              {playbookSlides.map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => goToSlide(idx)}
                  className={`w-2 h-2 rounded-full transition-all flex-shrink-0 ${
                    idx === currentSlide 
                      ? 'bg-amber-400 w-6' 
                      : idx < currentSlide 
                        ? 'bg-green-500' 
                        : 'bg-zinc-700 hover:bg-zinc-600'
                  }`}
                  title={s.title}
                />
              ))}
            </div>

            {/* Nav Buttons */}
            <div className="flex items-center justify-between gap-4">
              <Button
                onClick={prevSlide}
                disabled={currentSlide === 0}
                variant="outline"
                className="border-zinc-700 text-zinc-400 disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>

              <div className="hidden sm:flex items-center gap-2">
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                  {referralCode}
                </Badge>
                <Button size="sm" variant="ghost" onClick={copyCode} className="text-amber-400">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>

              <Button
                onClick={nextSlide}
                disabled={currentSlide === playbookSlides.length - 1}
                className="bg-amber-500 hover:bg-amber-600 text-black disabled:opacity-30"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
